import { defineComponent as _defineComponent } from 'vue'
import Vue, { computed, getCurrentInstance, onMounted, ref } from 'vue';
import VueObserveVisibility from 'vue-observe-visibility';

import TitleScoreList from '@/components/TitleScoreList.vue';
import PictureComp from '@/components/picture/PictureComp.vue';
import PosterGraphql from '@/components/poster/PosterGraphql.vue';
import BasicButton from '@/ui-components/BasicButton.vue';
import TitleDetailLink from '@/components/nav/links/TitleDetailLink.vue';
import { captureMessageForSentry } from '@/helpers/sentry-helper';

import { useQuery } from '@/helpers/composables/useApollo';
import {
	GetGlobalLargeSpotlightTitlesQuery,
	GetGlobalLargeSpotlightTitlesDocument,
	GetGlobalLargeSpotlightTitlesQueryVariables,
} from '@/pages/graphql/queries/GetGlobalLargeSpotlightTitles.query';

import { waitForRef } from '@/helpers/async-helper';
import { SnowplowContext, SnowplowOldModuleContext } from '@/helpers/tracking/providers';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { GlobalModuleName } from '@/helpers/composables/useGlobalTitles';
import { ImpressionTrackingEvents } from '@/enums/events';
import { defaultObservableOptions } from '@/mixins/types/impressionTracking';
import { faChevronLeft, faChevronRight, faInfoCircle } from '@fortawesome/free-solid-svg-icons';

type GlobalLargeSpotlightTitles = NonNullable<GetGlobalLargeSpotlightTitlesQuery['popularTitles']['edges']>;


export default /*#__PURE__*/_defineComponent({
  __name: 'GlobalLargeSpotlight',
  props: {
    language: null,
    country: null,
    moduleName: null
  },
  setup(__props: any) {

const props = __props as {
	language: string;
	country: string;
	moduleName: GlobalModuleName;
};

Vue.use(VueObserveVisibility);



const slider = ref<HTMLIonSlidesElement>();

const slideOpts = {
	initialSlide: 0,
	loop: true,
	autoplay: {
		delay: 3000,
		pauseOnMouseEnter: true,
	},
	grabCursor: true,
};

const queryVariables = computed(() => ({
	country: props.country,
	language: props.language,
	first: 7,
}));

const { result, onError } = useQuery<GetGlobalLargeSpotlightTitlesQuery, GetGlobalLargeSpotlightTitlesQueryVariables>(
	GetGlobalLargeSpotlightTitlesDocument,
	queryVariables,
	{ errorPolicy: 'all' }
);

const globalLargeSpotlightTitles = computed<GlobalLargeSpotlightTitles>(() => result.value?.popularTitles.edges ?? []);

const moduleContext = computed(() => new SnowplowOldModuleContext('', -1, props.moduleName, ''));

onError(error => {
	captureMessageForSentry(
		'[GraphQL Get Global Large Spotlight Titles Data]:',
		{ error: error, where: 'Component: GlobalLargeSpotlight' },
		'error'
	);
});

function getBackdrop(backdrops: { backdropUrl: string }[]) {
	return backdrops[0]?.backdropUrl || '';
}

function getHoursAndMinutes(minutes: number) {
	const hrs = Math.floor(minutes / 60);
	const mins = minutes % 60;

	return `${hrs}h ${mins}m`;
}

onMounted(async () => {
	const instance = getCurrentInstance()?.proxy!;
	await waitForRef(instance, 'slider');
	if (slider.value) {
		slider.value.options = slideOpts;
	}
});

function startAutoplay(isVisible: boolean) {
	if (isVisible) {
		slider.value?.startAutoplay();
	} else {
		slider.value?.stopAutoplay();
	}
}

function observeVisibilityOptions() {
	return {
		...defaultObservableOptions,
		callback: (isVisible: boolean) => {
			startAutoplay(isVisible);

			if (!isVisible) return;
			TrackingHelper.trackEvent(
				ImpressionTrackingEvents.MODULE,
				{
					action: 'impression',
					nonInteraction: true,
				},
				[moduleContext.value]
			);
		},
	};
}

function next() {
	slider.value?.slideNext();

	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'slider_clicked',
			label: 'right',
		},
		[moduleContext.value]
	);
}

function previous() {
	slider.value?.slidePrev();

	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'slider_clicked',
			label: 'left',
		},
		[moduleContext.value]
	);
}

function handleMoreInfoClick(title: any, contexts: SnowplowContext[] = []) {
	const titleContext = TrackingHelper.getTitleContextGraphql(
		title.node.objectId,
		title.node.objectType,
		title?.node?.content?.seasonNumber
	);

	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'more_info_clicked',
			label: 'featured',
		},
		[...contexts, titleContext, moduleContext.value]
	);
}

return { __sfc: true,props, slider, slideOpts, queryVariables, result, onError, globalLargeSpotlightTitles, moduleContext, getBackdrop, getHoursAndMinutes, startAutoplay, observeVisibilityOptions, next, previous, handleMoreInfoClick, TitleScoreList, PictureComp, PosterGraphql, BasicButton, TitleDetailLink, faChevronLeft, faChevronRight, faInfoCircle }
}

})